import { Component, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { App, AppsService } from '@app/shared/services/apps/apps.service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-apps-overview',
  templateUrl: './apps-overview.component.html',
  styleUrls: ['./apps-overview.component.scss'],
})
export class AppsOverviewComponent implements OnInit {
  integrations$: Observable<App[]> = of([]);
  integrationsCategories$ = this.appService.integrationsCategories$;
  tools$ = this.appService.tools$;

  searchCriteria = new BehaviorSubject<string>('');
  categories = new BehaviorSubject<string[]>([]);
  builtBy: BehaviorSubject<'hospitable' | 'partner' | null> = new BehaviorSubject(null);

  constructor(
    private appService: AppsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    this.route.queryParamMap
      .pipe(
        tap((queryParamMap) => {
          const categories = queryParamMap.getAll('categories');

          if (categories) {
            this.categories.next(categories);
          }
        })
      )
      .subscribe();

    this.integrations$ = combineLatest([this.categories, this.searchCriteria, this.builtBy]).pipe(
      switchMap(([categories, searchCriteria, builtBy]) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { categories },
          queryParamsHandling: 'merge',
        });

        return this.appService.integrations$.pipe(
          map((integrations) => {
            if (searchCriteria) {
              return this.filterBySearchCriteria(integrations, searchCriteria);
            } else {
              return this.filterByCategoryAndBuiltBy(integrations, categories, builtBy);
            }
          })
        );
      })
    );
  }

  private filterBySearchCriteria(integrations: App[], searchCriteria: string): App[] {
    return integrations.filter((integration) => {
      return (
        integration.title.toLowerCase().includes(searchCriteria.toLowerCase()) ||
        integration.title.toLowerCase() === 'missing an integration?' ||
        integration.title.toLowerCase() === 'building an integration?'
      );
    });
  }

  handleCategoryChange(event: MatCheckboxChange, category: string) {
    let categories = this.categories.value;

    if (event.checked) {
      categories = [...categories, category];
    } else {
      categories = categories.filter((c) => c !== category);
    }

    this.categories.next(categories);
  }

  private filterByCategoryAndBuiltBy(
    integrations: App[],
    category: string[] | null,
    builtBy: 'hospitable' | 'partner' | null
  ): App[] {
    return integrations
      .filter((integration) => {
        const isCategoryMatch = !category?.length || integration.categories?.some((c) => category.includes(c));
        const isBuiltByMatch = !builtBy || integration.builtBy === builtBy;
        return (
          (isCategoryMatch && isBuiltByMatch) ||
          integration.title.toLowerCase() === 'missing an integration?' ||
          integration.title.toLowerCase() === 'building an integration?'
        );
      })
      .sort(this.sortIntegrations);
  }

  sortIntegrations = (a, b) => {
    const nameA = a.title.toLowerCase();
    const nameB = b.title.toLowerCase();

    // Check if either title is one of the special cases.
    const isSpecialA = nameA === 'missing an integration?' || nameA === 'building an integration?';
    const isSpecialB = nameB === 'missing an integration?' || nameB === 'building an integration?';

    if (isSpecialA && !isSpecialB) {
      return 1; // Move nameA to the bottom.
    } else if (!isSpecialA && isSpecialB) {
      return -1; // Move nameB to the bottom.
    } else {
      return nameA.localeCompare(nameB); // Alphabetical sort for other cases.
    }
  };

  preventClose(event: MouseEvent): void {
    // Prevent the menu from closing when clicking on the checkbox
    event.stopPropagation();
  }
}
